import type { DepartmentCourse, DepartmentSection } from 'src/lib/agent-trpc';

// ----------------------------------------------------------------------
// Department page (epic #566) — pure wording and grouping over the server's numbers
// (`department.overview`). Nothing here re-derives health; it only orders and words it.

export type TeacherGroup = { teacherName: string; mine: boolean; sections: DepartmentSection[] };

/** Sections grouped by teacher, colleagues A–Z, the lead's own sections last. */
export function groupByTeacher(course: DepartmentCourse): TeacherGroup[] {
  const groups = new Map<string, TeacherGroup>();
  for (const s of course.sections) {
    const key = s.mine ? '\u0000mine' : s.teacherUid ?? s.teacherName;
    const g = groups.get(key) ?? { teacherName: s.teacherName, mine: s.mine, sections: [] };
    g.sections.push(s);
    groups.set(key, g);
  }
  return [...groups.values()].sort((a, b) =>
    a.mine === b.mine ? a.teacherName.localeCompare(b.teacherName) : a.mine ? 1 : -1
  );
}

export type PaceTone = 'success' | 'warning' | 'info' | 'default';

/** "On pace" / "3 lessons behind" / "1 lesson ahead" vs the course baseline. */
export function paceLabel(pace: number | null): { label: string; tone: PaceTone } {
  if (pace == null) return { label: 'No schedule', tone: 'default' };
  if (pace === 0) return { label: 'On pace', tone: 'success' };
  const n = Math.abs(pace);
  const lessons = `${n} lesson${n === 1 ? '' : 's'}`;
  return pace < 0 ? { label: `${lessons} behind`, tone: 'warning' } : { label: `${lessons} ahead`, tone: 'info' };
}

export type CourseTotals = { sections: number; students: number; behind: number; alerts: number };

export function courseTotals(course: DepartmentCourse): CourseTotals {
  return course.sections.reduce<CourseTotals>(
    (t, s) => ({
      sections: t.sections + 1,
      students: t.students + s.students,
      behind: t.behind + (s.pace != null && s.pace < 0 ? 1 : 0),
      alerts: t.alerts + s.alerts,
    }),
    { sections: 0, students: 0, behind: 0, alerts: 0 }
  );
}

/** The plural-aware attention pills for a section, in display order. */
export function attentionItems(s: DepartmentSection): { label: string; tone: 'info' | 'error' | 'warning' }[] {
  const out: { label: string; tone: 'info' | 'error' | 'warning' }[] = [];
  if (s.liveNow) out.push({ label: `${s.liveNow} in session now`, tone: 'info' });
  if (s.alerts) out.push({ label: `${s.alerts} alert${s.alerts === 1 ? '' : 's'}`, tone: 'error' });
  if (s.reviews) out.push({ label: `${s.reviews} to review`, tone: 'warning' });
  return out;
}
